import { apiClient } from '../apiClient';
import type { ApiResponse } from '../http/types';
import type {
  CreateRuleRequest,
  UpdateRuleRequest,
  RuleDetail,
  RuleSummary,
  SiteRulesResponse,
  SupportAlarmPathItem,
} from './ruleManagement.types';

const base = '/api/RuleManagement';

function normalizeUltraCriticalFlag<T extends Record<string, unknown>>(rule: T): T & { isUltraCritical: boolean } {
  return {
    ...rule,
    isUltraCritical: Boolean(rule.isUltraCritical ?? rule.isUltraCriticalAlarm ?? false),
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function extractRulesList(data: unknown): RuleSummary[] | null {
  if (Array.isArray(data)) {
    return data as RuleSummary[];
  }

  const record = asRecord(data);
  if (!record) {
    return null;
  }

  const directCandidates = [record.rules, record.siteRules, record.items, record.results];
  for (const candidate of directCandidates) {
    if (Array.isArray(candidate)) {
      return candidate as RuleSummary[];
    }
  }

  // Some backends nest payload one level deeper under "data"
  const nested = asRecord(record.data);
  if (nested) {
    const nestedCandidates = [nested.rules, nested.siteRules, nested.items, nested.results];
    for (const candidate of nestedCandidates) {
      if (Array.isArray(candidate)) {
        return candidate as RuleSummary[];
      }
    }
  }

  return null;
}

// Rules CRUD
export function createRule(payload: CreateRuleRequest): Promise<ApiResponse<RuleDetail>> {
  return apiClient.post<RuleDetail>(`${base}/rules`, payload).then(res => {
    if (res.success && res.data) {
      return {
        ...res,
        data: normalizeUltraCriticalFlag(res.data as Record<string, unknown>) as RuleDetail,
      };
    }

    return res;
  });
}

export function updateRule(ruleId: string | number, payload: UpdateRuleRequest): Promise<ApiResponse<RuleDetail>> {
  return apiClient.put<RuleDetail>(`${base}/rules/${encodeURIComponent(ruleId)}`, payload).then(res => {
    if (res.success && res.data) {
      return {
        ...res,
        data: normalizeUltraCriticalFlag(res.data as Record<string, unknown>) as RuleDetail,
      };
    }

    return res;
  });
}

export function getRule(ruleId: string | number): Promise<ApiResponse<RuleDetail>> {
  return apiClient.get<RuleDetail>(`${base}/rules/${encodeURIComponent(ruleId)}`).then(res => {
    if (res.success && res.data) {
      return {
        ...res,
        data: normalizeUltraCriticalFlag(res.data as Record<string, unknown>) as RuleDetail,
      };
    }

    return res;
  });
}

export function patchRuleStatus(ruleId: string | number, payload: Partial<RuleDetail>): Promise<ApiResponse<RuleSummary>> {
  return apiClient.patch<RuleSummary>(`${base}/rules/${encodeURIComponent(ruleId)}/status`, payload);
}

export function listAllRules(): Promise<ApiResponse<RuleSummary[]>> {
  return apiClient
    .get<unknown>(`${base}/rules_list?page=1&pageSize=500`)
    .then(res => {
      if (!res.success) {
        return { ...res, data: null };
      }

      const rules = extractRulesList(res.data);
      if (rules === null) {
        return {
          success: false,
          data: null,
          error: {
            code: 'INVALID_RESPONSE',
            message: 'Failed to load rules: unexpected response format',
          },
        };
      }

      const normalizedRules = rules.map(rule =>
        normalizeUltraCriticalFlag(rule as Record<string, unknown>) as RuleSummary
      );

      return {
        ...res,
        data: normalizedRules,
      };
    });
}

export async function deleteRule(ruleId: string | number): Promise<ApiResponse<void>> {
  try {
    const res = await apiClient.delete<void>(`${base}/rules/${encodeURIComponent(ruleId)}`);
    if (res?.success) {
      return res;
    }

    // If the server returned a method-not-allowed or not-found, fall back to patching the rule to inactive
    const status = res?.error?.status;
    if (status === 405 || status === 404 || !res.success) {
      try {
        const fallback = await patchRuleStatus(ruleId, { isActive: false });
        if (fallback?.success) {
          return { success: true, data: undefined } as ApiResponse<void>;
        }
        return { success: false, error: fallback?.error || { message: 'Failed to deactivate rule as fallback' } } as ApiResponse<void>;
      } catch (err: unknown) {
        console.info('[ruleManagement.service] deleteRule fallback error', ruleId, err);
        return { success: false, error: { message: (err as Error)?.message || String(err) } } as ApiResponse<void>;
      }
    }

    return res;
  } catch (err: unknown) {
    console.info('[ruleManagement.service] deleteRule error', ruleId, err);
    // Attempt fallback deactivation
    try {
      const fallback = await patchRuleStatus(ruleId, { isActive: false });
      if (fallback?.success) {
        return { success: true, data: undefined } as ApiResponse<void>;
      }
      return { success: false, error: fallback?.error || { message: 'Failed to delete or deactivate rule' } } as ApiResponse<void>;
    } catch (e: unknown) {
      return { success: false, error: { message: (e as Error)?.message || String(e) } } as ApiResponse<void>;
    }
  }
}

export function listSiteRules(siteIdentifierRegistryId: string | number): Promise<ApiResponse<SiteRulesResponse>> {
  if (typeof siteIdentifierRegistryId === 'string' && !/^[0-9]+$/.test(siteIdentifierRegistryId)) {
    console.info('[ruleManagement.service] listSiteRules: non-numeric identifier supplied, returning empty rules', siteIdentifierRegistryId);
    return Promise.resolve({
      success: true,
      data: {
        siteIdentifierRegistryId: Number(siteIdentifierRegistryId) || 0,
        siteHierarchyId: Number(siteIdentifierRegistryId) || 0,
        siteRules: [],
        totalRulesCount: 0,
        activeRulesCount: 0
      } as SiteRulesResponse
    } as ApiResponse<SiteRulesResponse>);
  }

  const cacheBuster = `_t=${Date.now()}`;
  return apiClient.get<SiteRulesResponse>(`${base}/sites/${encodeURIComponent(siteIdentifierRegistryId)}/rules?${cacheBuster}`)
    .then(res => {
      // Treat 404 (site not yet in RuleManagement) as empty rules list
      if (!res.success && res.error && res.error.status === 404) {
        return {
          success: true,
          data: {
            siteIdentifierRegistryId: Number(siteIdentifierRegistryId),
            siteHierarchyId: Number(siteIdentifierRegistryId),
            siteRules: [],
            totalRulesCount: 0,
            activeRulesCount: 0
          } as SiteRulesResponse
        } as ApiResponse<SiteRulesResponse>;
      }
      return res;
    })
    .catch(err => {
      console.info('[ruleManagement.service] listSiteRules error', siteIdentifierRegistryId, err);
      return {
        success: true,
        data: {
          siteIdentifierRegistryId: Number(siteIdentifierRegistryId),
          siteHierarchyId: Number(siteIdentifierRegistryId),
          siteRules: [],
          totalRulesCount: 0,
          activeRulesCount: 0
        } as SiteRulesResponse
      } as ApiResponse<SiteRulesResponse>;
    });
}

// Support endpoints - alarm paths
export function supportAlarmPaths(siteHierarchyId: string | number): Promise<ApiResponse<SupportAlarmPathItem[]>> {
  return apiClient.get<SupportAlarmPathItem[]>(`${base}/support/alarm-paths/${encodeURIComponent(siteHierarchyId)}`)
    .then(res => {
      // Treat 404 (no paths for site) as an empty list rather than an error so UI can handle gracefully
      if (!res.success && res.error && res.error.status === 404) {
        return { success: true, data: [] as SupportAlarmPathItem[] } as ApiResponse<SupportAlarmPathItem[]>;
      }
      return res;
    })
    .catch(err => {
      // In case the lower-level client throws, log and return empty list
      console.info('[ruleManagement.service] supportAlarmPaths error', siteHierarchyId, err);
      return { success: true, data: [] as SupportAlarmPathItem[] } as ApiResponse<SupportAlarmPathItem[]>;
    });
}
